import React, { useState, useEffect } from 'react';
import { Box, Alert, Container } from '@mui/material';
import { QuestionReportCard } from './QuestionReportCard';
import { SurveyReportDTO } from '../types';
import { REPORT_VIEWING_EVENTS, REPORT_ANALYTICS_EVENTS } from '../../../utils/trackingEvents';
import { usePostHog } from '../../../hooks/usePostHog';
import { ReportViewerSkeleton } from './skeletons/ReportViewerSkeleton';

interface ReportViewerProps {
  report: SurveyReportDTO;
  loading?: boolean;
  className?: string;
}

export const ReportViewer: React.FC<ReportViewerProps> = ({
  report,
  loading = false,
  className,
}) => {
  const { captureEvent } = usePostHog();
  const [reportStartTime] = useState<number>(Date.now());

  // Track report view on component mount
  useEffect(() => {
    if (report && !loading) {
      const chartTypesPresent =
        report.questionReports?.reduce((types: string[], qr) => {
          if (qr.chartConfig?.chartType && !types.includes(qr.chartConfig.chartType)) {
            types.push(qr.chartConfig.chartType);
          }
          return types;
        }, []) || [];

      captureEvent(REPORT_VIEWING_EVENTS.REPORT_VIEWED, {
        report_id: report.id,
        survey_id: report.surveyId,
        survey_title: report.title || 'Unknown',
        audit_id: report.auditId,
        total_questions: report.questionReports?.length || 0,
        included_responses: report.includedResponses,
        total_comments:
          report.questionReports?.reduce(
            (sum, qr) => sum + (qr.includedComments?.length || 0),
            0
          ) || 0,
        chart_types_present: chartTypesPresent,
        questions_with_data:
          report.questionReports?.filter(
            (qr) => qr.chartConfig || (qr.includedComments?.length || 0) > 0
          ).length || 0,
        report_load_time_ms: Date.now() - reportStartTime,
      });

      // Track large dataset rendering
      const totalDataPoints =
        report.questionReports?.reduce((sum, qr) => {
          return (
            sum +
            (qr.chartConfig?.data?.datasets?.[0]?.data?.reduce((s, val) => s + val, 0) || 0) +
            (qr.includedComments?.length || 0)
          );
        }, 0) || 0;

      if (totalDataPoints > 500) {
        captureEvent(REPORT_ANALYTICS_EVENTS.LARGE_DATASET_RENDERED, {
          report_id: report.id,
          total_data_points: totalDataPoints,
          total_questions: report.questionReports?.length || 0,
          included_responses: report.includedResponses,
        });
      }
    }
  }, [report, loading, reportStartTime, captureEvent]);

  // Track engagement on page unload
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (report && !loading) {
        const sessionDuration = (Date.now() - reportStartTime) / 1000;
        captureEvent(REPORT_ANALYTICS_EVENTS.REPORT_SESSION_SUMMARY, {
          report_id: report.id,
          session_duration_seconds: sessionDuration,
          total_questions: report.questionReports?.length || 0,
          included_responses: report.includedResponses,
        });
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [report, loading, reportStartTime, captureEvent]);

  if (loading) {
    return <ReportViewerSkeleton questionCount={3} showHeader={false} />;
  }

  if (!report || !report.questionReports || report.questionReports.length === 0) {
    return (
      <Box>
        <Alert severity="warning">
          No report data available or the report contains no questions.
        </Alert>
      </Box>
    );
  }

  // Sort questions by order
  const sortedQuestions = [...report.questionReports].sort((a, b) => a.order - b.order);

  return (
    <Box className={className}>
      {/* Banner Spacer Zone — matches SurveyBuilder/SurveyAuditPage for consistent vertical alignment */}
      <Box sx={{ minHeight: 72 }} />

      {/* Questions Section */}
      <Container maxWidth={false} disableGutters>
        <Box>
          {sortedQuestions.map((questionReport, _index) => (
            <Box key={questionReport.questionId}>
              <QuestionReportCard
                questionReport={questionReport}
                showComments={true}
                chartSize="large"
                className="print-question-card"
              />
            </Box>
          ))}
        </Box>
      </Container>

      {/* Print-specific styles */}
      <style>{`
        @media print {
          .print-question-card {
            page-break-inside: avoid;
            page-break-after: auto;
          }
          
          @page {
            margin: 20mm;
            size: A4;
          }
          
          body {
            -webkit-print-color-adjust: exact;
            color-adjust: exact;
            print-color-adjust: exact;
          }
          
          .MuiButton-root,
          .print-hide {
            display: none !important;
          }
          
          .MuiPaper-root {
            box-shadow: none !important;
            border: 1px solid #ddd !important;
          }
          
          .recharts-wrapper {
            page-break-inside: avoid;
          }
        }
      `}</style>
    </Box>
  );
};
